<template>
  <div class="card speaking-assessment-card mb-4">
    <div class="card-body">
      <div class="section-heading mb-4">Đánh giá bài làm</div>

      <div v-if="type === 'writing'" class="plagiarism-box mb-4">
        <div class="plagiarism-box__icon">
          <i class="fas fa-copy"></i>
        </div>

        <div class="plagiarism-box__content">
          <div class="plagiarism-box__title">Kiểm tra đạo văn bài viết</div>
          <div class="plagiarism-box__text">
            Vui lòng truy cập liên kết bên cạnh để kiểm tra đạo văn và đánh giá trước khi nhận xét, chấm bài.
          </div>
        </div>

        <button
          type="button"
          class="btn btn-primary plagiarism-box__button"
          :disabled="disable"
          @click="handlePlagiarismCheck"
        >
          Kiểm tra đạo văn
        </button>
      </div>

      <div class="assessment-choice-list">
        <label class="assessment-choice-item mb-3 mb-md-0" :class="{ 'is-disabled': disable }">
          <input
            type="radio"
            value="pass"
            :disabled="disable"
            :checked="evaluationStatus === 'pass'"
            @change="emitSpeakingEvaluationUpdate('evaluationStatus', 'pass')"
          >
          <span class="choice-indicator"></span>
          <span class="choice-label">Đạt chuẩn</span>
        </label>

        <label class="assessment-choice-item align-items-start" :class="{ 'is-disabled': disable }">
          <input
            type="radio"
            value="fail"
            :disabled="disable"
            :checked="evaluationStatus === 'fail'"
            @change="emitSpeakingEvaluationUpdate('evaluationStatus', 'fail')"
          >
          <span class="choice-indicator mt-1"></span>
          <span class="choice-label mr-2">Không đạt chuẩn</span>
          <span class="tooltip-anchor">
            <img :src="tooltipIcon" alt="tooltip" class="tooltip-icon">
            <span class="tooltip-bubble">
              Chọn "Không đạt chuẩn", hệ thống sẽ tự động chấm 0 điểm cho tất cả các bài trong kỹ năng này.
            </span>
          </span>
        </label>
      </div>

      <div v-if="evaluationStatus === 'fail'" class="mt-4 pt-3 border-top">
        <div class="form-group mb-3">
          <label class="field-label">
            Lý do không đạt chuẩn <span class="text-danger">*</span>
          </label>

          <div class="position-relative">
            <button
              type="button"
              class="btn btn-light border speaking-dropdown text-left"
              :class="{ 'is-invalid': validationError }"
              :disabled="disable"
              @click="handleDropdownClick"
            >
              <span :class="notMeetReason ? 'text-dark' : 'text-muted'">{{ displayReasonText }}</span>
              <span class="dropdown-arrow">
                <i class="fas fa-chevron-down"></i>
              </span>
            </button>

            <div v-show="isReasonOpen" class="dropdown-menu speaking-dropdown-menu show">
              <button
                v-for="(reason, idx) in reasonsNotMeet"
                :key="idx"
                type="button"
                class="dropdown-item"
                :disabled="disable"
                :class="{ active: notMeetReason == reason.value || notMeetReason === reason.label }"
                @click="handleReasonSelect(reason)"
              >
                {{ reason.label }}
              </button>
            </div>
          </div>

          <div v-if="validationError" class="invalid-feedback d-block">
            {{ validationError }}
          </div>
        </div>

        <div class="form-group mb-0">
          <label class="field-label">Mô tả chi tiết</label>
          <textarea
            :value="notMeetDescription"
            :disabled="disable"
            rows="4"
            class="form-control speaking-textarea"
            placeholder="Nhập chi tiết lý do, minh chứng bài làm không đạt chuẩn"
            @input="handleDescriptionInput"
          ></textarea>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import tooltipIcon from '@/assets/img/tooltip.png'
import { REASONS_BY_TYPE } from '../constant'

export default {
  name: 'SpeakingAssessment',
  props: {
    evaluationStatus: {
      type: String,
      default: 'pass'
    },
    notMeetReason: {
      type: String,
      default: ''
    },
    notMeetDescription: {
      type: String,
      default: ''
    },
    forceValidate: {
      type: Boolean,
      default: false
    },
    disable: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'speaking'
    }
  },
  data() {
    return {
      tooltipIcon: tooltipIcon,
      isReasonOpen: false,
      reasonTouched: false
    }
  },
  computed: {
    reasonsNotMeet() {
      return REASONS_BY_TYPE[this.type] || REASONS_BY_TYPE.speaking
    },
    displayReasonText() {
      if (!this.notMeetReason) {
        return 'Chọn lý do không đạt chuẩn'
      }

      const selectedReason = this.reasonsNotMeet.find((reason) => {
        return reason.value == this.notMeetReason || reason.label == this.notMeetReason
      })

      return selectedReason ? selectedReason.label : 'Chọn lý do không đạt chuẩn'
    },
    validationError() {
      if (this.evaluationStatus !== 'fail' || !this.reasonTouched) {
        return ''
      }

      return this.notMeetReason ? '' : 'Vui lòng chọn lý do không đạt chuẩn'
    }
  },
  watch: {
    evaluationStatus(newValue) {
      if (newValue !== 'fail') {
        this.isReasonOpen = false
        this.reasonTouched = false
      }
    },
    forceValidate(newValue) {
      if (newValue && this.evaluationStatus === 'fail') {
        this.reasonTouched = true
      }
    },
    disable(newValue) {
      if (newValue) {
        this.isReasonOpen = false
      }
    }
  },
  methods: {
    emitSpeakingEvaluationUpdate(type, value) {
      if (this.disable) {
        return
      }

      this.$emit('update:speakingEvaluation', { type: type, value: value })
      if (type === 'evaluationStatus' && value === 'fail') {
        this.reasonTouched = true
      }
    },
    handleReasonSelect(reason) {
      if (this.disable) {
        return
      }

      this.reasonTouched = true
      this.emitSpeakingEvaluationUpdate('notMeetReason', reason.label)
      this.isReasonOpen = false
    },
    handleDropdownClick() {
      if (this.disable) {
        return
      }

      this.reasonTouched = true
      this.isReasonOpen = !this.isReasonOpen
    },
    handleDescriptionInput(event) {
      if (this.disable) {
        return
      }

      this.emitSpeakingEvaluationUpdate('notMeetDescription', event.target.value)
    },
    handlePlagiarismCheck() {
      window.open('https://plagiarismdetector.net', '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
.speaking-assessment-card {
  border: 1px solid #e4e6ef;
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1.5rem rgba(140, 152, 164, 0.08);
}

.section-heading {
  font-size: 1.05rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #3f4254;
  letter-spacing: 0.04em;
}

.field-label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.975rem;
  font-weight: 600;
  color: #3f4254;
}

.plagiarism-box {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid #e4e6ef;
  border-radius: 0.75rem;
  background: #f8f9fc;
}

.plagiarism-box__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #5867dd 0%, #3699ff 100%);
  color: #fff;
  font-size: 1rem;
  flex: 0 0 auto;
}

.plagiarism-box__content {
  flex: 1 1 auto;
  min-width: 0;
}

.plagiarism-box__title {
  font-size: 0.975rem;
  font-weight: 700;
  color: #3f4254;
}

.plagiarism-box__text {
  margin-top: 0.25rem;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #7e8299;
}

.plagiarism-box__button {
  flex: 0 0 auto;
  white-space: nowrap;
  border-radius: 0.475rem;
  font-weight: 600;
}

.assessment-choice-list {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
}

.assessment-choice-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin: 0;
  cursor: pointer;
  color: #3f4254;
}

.assessment-choice-item.is-disabled {
  cursor: not-allowed;
  opacity: 0.75;
}

.assessment-choice-item input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.choice-indicator {
  position: relative;
  width: 1.125rem;
  height: 1.125rem;
  margin-right: 0.625rem;
  border: 2px solid #c4c8d4;
  border-radius: 50%;
  background: #fff;
  flex: 0 0 auto;
}

.assessment-choice-item input:checked + .choice-indicator {
  border-color: #5867dd;
}

.assessment-choice-item input:checked + .choice-indicator::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #5867dd;
  transform: translate(-50%, -50%);
}

.choice-label {
  font-size: 1rem;
  font-weight: 500;
}

.tooltip-anchor {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.tooltip-icon {
  width: 1rem;
  height: 1rem;
  object-fit: contain;
}

.tooltip-bubble {
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  z-index: 20;
  min-width: 280px;
  max-width: 360px;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  background: #3f4254;
  color: #fff;
  font-size: 0.875rem;
  line-height: 1.5;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-50%);
  transition: opacity 0.15s ease;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.tooltip-anchor:hover .tooltip-bubble {
  opacity: 1;
  visibility: visible;
}

.speaking-dropdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 42px;
  padding: 0.625rem 0.875rem;
  font-size: 0.975rem;
  border-color: #e4e6ef;
  border-radius: 0.475rem;
}

.speaking-dropdown:focus,
.speaking-dropdown.is-invalid {
  box-shadow: none;
}

.dropdown-arrow {
  margin-left: 0.75rem;
  color: #7e8299;
  font-size: 0.75rem;
}

.speaking-dropdown-menu {
  width: 100%;
  margin-top: 0.25rem;
  border: 1px solid #e4e6ef;
  border-radius: 0.475rem;
  box-shadow: 0 0.5rem 1.5rem rgba(140, 152, 164, 0.15);
}

.speaking-dropdown-menu .dropdown-item {
  font-size: 0.95rem;
  padding: 0.625rem 0.875rem;
}

.speaking-dropdown-menu .dropdown-item.active,
.speaking-dropdown-menu .dropdown-item:active {
  background: #f3f6f9;
  color: #5867dd;
}

.speaking-textarea.form-control {
  min-height: 110px;
  border-color: #e4e6ef;
  border-radius: 0.475rem;
  resize: vertical;
  font-size: 0.975rem;
  line-height: 1.65;
}

@media (max-width: 767.98px) {
  .plagiarism-box {
    align-items: flex-start;
    flex-direction: column;
  }

  .plagiarism-box__button {
    width: 100%;
  }

  .tooltip-bubble {
    left: 0;
    top: calc(100% + 10px);
    transform: none;
    min-width: 240px;
  }
}
</style>
